import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, findIndex } from 'rxjs';
import Project from 'src/app/interfaces/project';
import {
  FETCH_PROJECT_DISPOSITIONS,
  ProjectDispositionResponse,
  CREATE_DISPOSITION,
  UPDATE_DISPOSITION,
  DispositionInput,
  DispositionDateInput,
  UpsertDispositionStatus,
  CREATE_DISPOSITION_DISTRIBUTION,
  DispositionDistributionProceedInput,
  CREATE_DISPOSITION_DISTRIBUTION_PROCEED,
  UpdateDispositionDistributionInput,
  UPDATE_DISPOSITION_DISTRIBUTION,
  DELETE_DISPOSITION,
  DELETE_DISPOSITION_DISTRIBUTION,
  DELETE_DISPOSITION_DISTRIBUTION_PROCEED,
} from './graphql/dispositions';
import DispositionType from 'src/app/interfaces/disposition-type';
import DispositionBov from 'src/app/interfaces/disposition-bov';
import DispositionLegalStatus from 'src/app/interfaces/disposition-legal-status';
import DispositionYieldRequestStatus from 'src/app/interfaces/disposition-yield-request-status';
import Status from 'src/app/interfaces/status';
import Disposition from 'src/app/interfaces/disposition';
import DateType from 'src/app/interfaces/date-type';
import DateClass from 'src/app/interfaces/date-class';
import DispositionPipelineDate from 'src/app/interfaces/disposition-pipeline-date';
import DispositionProceedType from 'src/app/interfaces/disposition_proceed_type';
import { DispositionInfoTableComponent } from '../deals-subcomponents/dispositions/disposition-info-table/disposition-info-table.component';
import Permission from 'src/app/interfaces/permission';
import { AlertService } from '../../alert/alert.service';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import disposition from 'src/app/interfaces/disposition';
import DispositionDistribution from '../../interfaces/disposition-distribution';
import DispositionDistributionProceed from '../../interfaces/disposition-distribution-proceed';

const config = new MatSnackBarConfig();
config.panelClass = ['save-success'];
config.horizontalPosition = 'center';
config.verticalPosition = 'top';
config.duration = 3000;
@Injectable({
  providedIn: 'root',
})
export class DealDispositionsService {
  constructor(
    private apollo: Apollo,
    private alert: AlertService,
    private _snackBar: MatSnackBar
  ) {}

  private _project: Project = { id: '', name: '', __typename: 'Project' };
  private project: BehaviorSubject<Project> = new BehaviorSubject<Project>(
    this._project
  );
  public $project: Observable<Project> = this.project.asObservable();
  private _dispositions: Disposition[] = [];
  private dispositions: BehaviorSubject<Disposition[]> = new BehaviorSubject<
    Disposition[]
  >(this._dispositions);
  public dispositions$: Observable<Disposition[]> =
    this.dispositions.asObservable();
  private _dispositionTypes: DispositionType[] = [];
  private dispositionTypes: BehaviorSubject<DispositionType[]> =
    new BehaviorSubject<DispositionType[]>(this._dispositionTypes);
  public dispositionTypes$: Observable<DispositionType[]> =
    this.dispositionTypes.asObservable();
  private _dispositionBovs: DispositionBov[] = [];
  private dispositionBovs: BehaviorSubject<DispositionBov[]> =
    new BehaviorSubject<DispositionBov[]>(this._dispositionBovs);
  public dispositionBovs$: Observable<DispositionBov[]> =
    this.dispositionBovs.asObservable();
  private _dispositionLegalStatuses: DispositionLegalStatus[] = [];
  private dispositionLegalStatuses: BehaviorSubject<DispositionLegalStatus[]> =
    new BehaviorSubject<DispositionLegalStatus[]>(
      this._dispositionLegalStatuses
    );
  public dispositionLegalStatuses$: Observable<DispositionLegalStatus[]> =
    this.dispositionLegalStatuses.asObservable();
  private _dispositionYieldRequestStatuses: DispositionYieldRequestStatus[] =
    [];
  private dispositionYieldRequestStatuses: BehaviorSubject<
    DispositionYieldRequestStatus[]
  > = new BehaviorSubject<DispositionYieldRequestStatus[]>(
    this._dispositionYieldRequestStatuses
  );
  public dispositionYieldRequestStatuses$: Observable<
    DispositionYieldRequestStatus[]
  > = this.dispositionYieldRequestStatuses.asObservable();
  private _dispositionStatuses: Status[] = [];
  private dispositionStatuses: BehaviorSubject<Status[]> = new BehaviorSubject<
    Status[]
  >(this._dispositionStatuses);
  public dispositionStatuses$: Observable<Status[]> =
    this.dispositionStatuses.asObservable();
  private _dateTypes: DateType[] = [];
  private dateTypes: BehaviorSubject<DateType[]> = new BehaviorSubject<
    DateType[]
  >(this._dateTypes);
  public dateTypes$: Observable<DateType[]> = this.dateTypes.asObservable();
  private _dateClass: DateClass | null = null;
  private dateClass: BehaviorSubject<DateClass | null> =
    new BehaviorSubject<DateClass | null>(this._dateClass);
  public dateClass$: Observable<DateClass | null> =
    this.dateClass.asObservable();
  private _dispositionProceedTypes: DispositionProceedType[] = [];
  private dispositionProceedTypes = new BehaviorSubject<
    DispositionProceedType[]
  >(this._dispositionProceedTypes);
  public dispositionProceedTypes$ = this.dispositionProceedTypes.asObservable();

  private _permission: Permission | null = null;
  private permission = new BehaviorSubject(this._permission);
  public permission$ = this.permission.asObservable();

  public getDealDisposition(id: string): Observable<Project> {
    let sub = this.apollo
      .watchQuery({
        query: FETCH_PROJECT_DISPOSITIONS,
        variables: {
          id: id,
        },
      })
      .valueChanges.subscribe(({ data }) => {
        if (!data) return;
        this._project = structuredClone(data.project) as Project;
        this._dispositions = structuredClone(
          data.getDispositions
        ) as Disposition[];
        this._dispositionBovs = structuredClone(
          data.dispositionBovs
        ) as DispositionBov[];
        this._dispositionLegalStatuses = structuredClone(
          data.dispositionLegalStatuses
        ) as DispositionLegalStatus[];
        this._dispositionTypes = structuredClone(
          data.dispositionTypes
        ) as DispositionType[];
        this._dispositionYieldRequestStatuses = structuredClone(
          data.dispositionYieldRequestStatuses
        ) as DispositionYieldRequestStatus[];
        this._dispositionStatuses = structuredClone(
          data.statusesBySequence
        ) as Status[];
        this._dispositionStatuses = structuredClone(
          [...this._dispositionStatuses].sort((a, b) => {
            if (
              a.statusSequenceDetails &&
              a.statusSequenceDetails.length &&
              b.statusSequenceDetails &&
              b.statusSequenceDetails.length
            )
              return (
                a.statusSequenceDetails[0].index -
                b.statusSequenceDetails[0].index
              );
            return 0;
          })
        );
        this._dateTypes = structuredClone(data.dateTypes) as DateType[];
        this._dateClass = structuredClone(
          data.date_class_by_name
        ) as DateClass | null;
        this._dispositionProceedTypes = structuredClone(
          data.dispositionProceedTypes
        ) as DispositionProceedType[];
        this.setPermission(
          (structuredClone(data.me?.permissionsForResource) as Permission | null) ??
            null
        );

        this.dateClass.next(this._dateClass);
        this.dateTypes.next(this._dateTypes);
        this.project.next(this._project);
        this.dispositions.next(this._dispositions);
        this.dispositionBovs.next(this._dispositionBovs);
        this.dispositionLegalStatuses.next(this._dispositionLegalStatuses);
        this.dispositionTypes.next(this._dispositionTypes);
        this.dispositionStatuses.next(this._dispositionStatuses);
        this.dispositionProceedTypes.next(this._dispositionProceedTypes);
        this.dispositionYieldRequestStatuses.next(
          this._dispositionYieldRequestStatuses
        );
        sub.unsubscribe();
      });

    return this.$project;
  }
  private setPermission(permission: Permission | null) {
    this._permission = permission;
    this.permission.next(permission);
  }
  public createDisposition(projectId: string) {
    let sub = this.apollo
      .mutate({
        mutation: CREATE_DISPOSITION,
        variables: {
          project_id: projectId,
        },
      })
      .subscribe(({ data, loading }) => {
        let disposition = structuredClone(data?.createDisposition);
        let index = this._dispositions.findIndex(
          (value) => value.id === disposition?.id
        );
        if (index > -1 && disposition) {
          this._dispositions[index] = disposition;
        } else if (disposition) {
          this._dispositions.unshift(disposition);
        }
        this.dispositions.next(this._dispositions);
        sub.unsubscribe();
      });
  }
  public updateDisposition(
    id: string,
    dispositionInput: DispositionInput,
    dispositionDatesInput: DispositionDateInput[],
    dispositionPipelineDates: DispositionPipelineDate[],
    dispositionStatusInput: UpsertDispositionStatus | null
  ) {
    let dispositionSub = this.apollo
      .mutate({
        mutation: UPDATE_DISPOSITION,
        variables: {
          id,
          input: dispositionInput,
          dispositionDatesInput,
          dispositionPipelineDates,
          dispositionStatusInput,
        },
      })
      .subscribe(({ data, loading }) => {
        let disposition = structuredClone(data?.updateDisposition);
        let index = this._dispositions.findIndex(
          (value) => value.id === disposition?.id
        );
        if (index && disposition) {
          this._dispositions[index] = disposition;
          this.dispositions.next(this._dispositions);
        }
        this._snackBar.open(
          'Disposition Info saved successfully',
          'Ok',
          config
        );
        dispositionSub.unsubscribe();
      });
  }
  public createDispositionDistribution(dispositionId: string) {
    let sub = this.apollo
      .mutate({
        mutation: CREATE_DISPOSITION_DISTRIBUTION,
        variables: { dispositionId },
      })
      .subscribe(({ data, loading }) => {
        if (data?.createDispositionDistribution) {
          let distribution: DispositionDistribution = structuredClone(
            data.createDispositionDistribution
          );
          let index = this._dispositions.findIndex(
            (disposition) => disposition.id === distribution.disposition_id
          );
          if (index > -1 && distribution) {
            let disposition = structuredClone(this._dispositions[index]);
            disposition.dispositionDistributions
              ? disposition.dispositionDistributions?.push(distribution)
              : (disposition.dispositionDistributions = [distribution]);
            this._dispositions[index] = disposition;
            this.dispositions.next(this._dispositions);
          }
        }
        this._snackBar.open('Distribution added successfully', 'Ok', config);
        sub?.unsubscribe();
      });
  }
  public createDispositionDistributionProceed(
    dispositionDistributionProceedInput: DispositionDistributionProceedInput
  ) {
    let sub = this.apollo
      .mutate({
        mutation: CREATE_DISPOSITION_DISTRIBUTION_PROCEED,
        variables: {
          dispositionDistributionProceedInput,
        },
      })
      .subscribe(({ data, loading }) => {
        if (data?.createDispositionDistributionProceed) {
          let proceed: DispositionDistributionProceed = structuredClone(
            data.createDispositionDistributionProceed
          );
          let distributionIndex = -1;
          let index = this._dispositions.findIndex((disposition) => {
            distributionIndex =
              disposition.dispositionDistributions?.findIndex(
                (distribution) =>
                  distribution.id === proceed.disposition_distribution_id
              ) ?? -1;
            return distributionIndex > -1;
          });
          if (index > -1 && distributionIndex > -1) {
            let disposition = structuredClone(this._dispositions[index]);
            if (disposition.dispositionDistributions) {
              disposition.dispositionDistributions[
                distributionIndex
              ].dispositionDistributionProceeds.push(proceed);
            }
            this._dispositions[index] = disposition;
            this.dispositions.next(this._dispositions);
          }
        }
        this._snackBar.open('Proceed added successfully', 'Ok', config);
        sub?.unsubscribe();
      });
  }
  public dispositionDistributionChanged(data: {
    id: string;
    total: number;
    distribution_date: string | null;
    dispositionDistributionProceeds: DispositionDistributionProceed[];
  }) {
    let { id, total, distribution_date, dispositionDistributionProceeds } =
      data;
    let distributionIndex = -1;
    let index = this._dispositions.findIndex((disposition) => {
      if (disposition.dispositionDistributions) {
        distributionIndex = disposition.dispositionDistributions.findIndex(
          (distribution) => distribution.id === id
        );
      }
      return distributionIndex > -1;
    });
    if (index > -1 && distributionIndex > -1) {
      let disposition = structuredClone(this._dispositions[index]);
      let distribution = structuredClone(
        disposition.dispositionDistributions?.[distributionIndex]
      );
      if (distribution && disposition.dispositionDistributions) {
        distribution.total = total;
        distribution.distribution_date = distribution_date;
        distribution.dispositionDistributionProceeds = structuredClone(
          dispositionDistributionProceeds
        );
        disposition.dispositionDistributions[distributionIndex] = distribution;
        this._dispositions[index] = disposition;
      }

      this.dispositions.next(this._dispositions);
    }
  }
  public updateDispositionDistribution(
    id: string,
    input: UpdateDispositionDistributionInput
  ) {
    let sub = this.apollo
      .mutate({
        mutation: UPDATE_DISPOSITION_DISTRIBUTION,
        variables: { id, input },
      })
      .subscribe(({ data, loading }) => {
        if (data?.updateDispositionDistribution) {
          let distribution: DispositionDistribution = structuredClone(
            data.updateDispositionDistribution
          );
          this.dispositionDistributionChanged(distribution);
        }
        this._snackBar.open(
          'Distribution with proceeds saved successfully',
          'Ok',
          config
        );
        sub?.unsubscribe();
      });
  }
  public deleteDisposition(id: string) {
    let sub = this.apollo
      .mutate({
        mutation: DELETE_DISPOSITION,
        variables: {
          id,
        },
      })
      .subscribe(({ data, loading }) => {
        let id = (data as { deleteDisposition?: { id?: string } })
          ?.deleteDisposition?.id;
        if (id) {
          this._dispositions = this._dispositions.filter(
            (disposition) => disposition.id !== id
          );
          this.dispositions.next(this._dispositions);
        }
        this._snackBar.open('Disposition deleted successfully', 'Ok', config);
        sub?.unsubscribe();
      });
  }
  public deleteDistribution(id: string) {
    let sub = this.apollo
      .mutate({ mutation: DELETE_DISPOSITION_DISTRIBUTION, variables: { id } })
      .subscribe(({ data, loading }) => {
        let id = (data as { deleteDispositionDistribution?: { id?: string } })
          ?.deleteDispositionDistribution?.id;
        if (id) {
          let distributionIndex = -1;
          let index = this._dispositions.findIndex((disposition) => {
            if (disposition.dispositionDistributions) {
              distributionIndex =
                disposition.dispositionDistributions.findIndex(
                  (distribution) => distribution.id === id
                );
            }
            return distributionIndex > -1;
          });
          if (index > -1 && distributionIndex > -1) {
            let disposition = structuredClone(this._dispositions[index]);
            if (disposition.dispositionDistributions) {
              disposition.dispositionDistributions =
                disposition.dispositionDistributions.filter(
                  (distribution) => distribution.id !== id
                );
            }
            this._dispositions[index] = disposition;
            this.dispositions.next(this._dispositions);
          }
        }
        this._snackBar.open('Distribution deleted successfully', 'Ok', config);
        sub?.unsubscribe();
      });
  }
  public deleteProceed(id: string) {
    let sub = this.apollo
      .mutate({
        mutation: DELETE_DISPOSITION_DISTRIBUTION_PROCEED,
        variables: { id },
      })
      .subscribe(({ data, loading }) => {
        let id = (
          data as { deleteDispositionDistributionProceed?: { id?: string } }
        )?.deleteDispositionDistributionProceed?.id;
        if (id) {
          let distributionIndex = -1;
          let index = this._dispositions.findIndex((disposition) => {
            if (disposition.dispositionDistributions) {
              distributionIndex =
                disposition.dispositionDistributions.findIndex((distribution) =>
                  distribution.dispositionDistributionProceeds.find(
                    (proceed) => proceed.id === id
                  )
                );
            }
            return distributionIndex > -1;
          });
          if (index > -1 && distributionIndex > -1) {
            let disposition = structuredClone(this._dispositions[index]);
            if (disposition.dispositionDistributions) {
              let distribution = structuredClone(
                disposition.dispositionDistributions[distributionIndex]
              );
              distribution.dispositionDistributionProceeds =
                distribution.dispositionDistributionProceeds.filter(
                  (proceed) => proceed.id !== id
                );
              disposition.dispositionDistributions[distributionIndex] =
                distribution;
              this._dispositions[index] = disposition;
              this.dispositions.next(this._dispositions);
            }
          }
        }
        this._snackBar.open('Proceed deleted successfully', 'Ok', config);
        sub?.unsubscribe();
      });
  }
}
